/**
 * The course map (pedagogy spec §3.3.5 item 3): one row per chapter that has an eligible topic,
 * one 28 px tile per topic, its state drawn as a fill and a glyph and spelled out in the tile's
 * name ("Tidsdilatation: på egen hand").
 *
 * The tiles form one `role="grid"` with a single Tab stop and a roving `tabindex`. The detail
 * panel (non-modal, `aria-live`) sits under the grid, and the per-chapter disclosures that open
 * the topic tables sit after both, outside the grid, so no table or stray focusable lives inside
 * a row. Keys are handled on the tiles and the panel only; the page has no window shortcuts.
 */
import clsx from "clsx";
import { useId, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "react-router";
import { useLocalizedText } from "@client/components/Localized";
import { Card } from "@client/components/ui";
import { practiceDeepLink } from "@client/features/practice/deepLink";
import { useLang, useT } from "@client/i18n";
import { TOPIC_STATES, type TopicState } from "@shared/evidence";
import type { Topic } from "@shared/types";
import { ChaptersSection, ChapterSummary, useChapterTitle } from "./ChaptersSection";
import { useDayLabel } from "./dayLabel";
import { StateGlyph, StateSwatch, stateTileClass } from "./stateStyle";
import type { ChapterDto, ChapterProgress, TopicProgress } from "./types";

interface MapTile {
  topic: TopicProgress;
  state: TopicState;
}

interface MapRow {
  chapter: ChapterProgress;
  tiles: MapTile[];
}

export interface CourseMapProps {
  chapters: ChapterProgress[];
  chapterInfos: ChapterDto[];
  topics: Topic[];
  /** The day key "idag" is read from; defaults to the clock. */
  today?: string;
}

/** The chapters with at least one eligible topic, with their eligible topics in course order. */
function mapRows(chapters: readonly ChapterProgress[]): MapRow[] {
  return chapters
    .map((chapter) => ({
      chapter,
      tiles: chapter.topics.flatMap((topic) =>
        topic.eligible && topic.state !== null ? [{ topic, state: topic.state }] : [],
      ),
    }))
    .filter((row) => row.tiles.length > 0);
}

export function CourseMap({ chapters, chapterInfos, topics, today }: CourseMapProps) {
  const t = useT();
  const { lang } = useLang();
  const localize = useLocalizedText();
  const chapterTitle = useChapterTitle();
  const dayLabel = useDayLabel(today);
  const panelId = useId();
  const instructionsId = useId();
  const tileRefs = useRef(new Map<string, HTMLButtonElement>());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);

  const rows = mapRows(chapters);
  const infoByChapter = new Map(chapterInfos.map((info) => [info.number, info]));
  const topicById = new Map(topics.map((topic) => [topic.id, topic]));
  const nameOf = (topicId: string): string => {
    const topic = topicById.get(topicId);
    return topic ? localize(topic.name) : topicId;
  };
  // "Tidsdilatation: på egen hand": the state names are capitalised for the legend and panel.
  const labelOf = (tile: MapTile): string =>
    t("progress.map.tileLabel", {
      topic: nameOf(tile.topic.topicId),
      state: t(`progress.map.state.${tile.state}`).toLocaleLowerCase(lang),
    });

  const allTiles = rows.flatMap((row) => row.tiles);
  // The one tile in the Tab order: the last one focused, else the first.
  const tabStopId =
    allTiles.find((tile) => tile.topic.topicId === focusId)?.topic.topicId ??
    allTiles[0]?.topic.topicId;
  const selected = allTiles.find((tile) => tile.topic.topicId === selectedId);

  function focusTile(topicId: string) {
    setFocusId(topicId);
    tileRefs.current.get(topicId)?.focus();
  }

  function closePanel() {
    const id = selectedId;
    setSelectedId(null);
    // The panel unmounts with its focus; the tile that opened it takes it back.
    if (id) tileRefs.current.get(id)?.focus();
  }

  function onTileKeyDown(event: KeyboardEvent<HTMLButtonElement>, rowIndex: number, col: number) {
    // Alt+←/→ is the browser's Back/Forward, Ctrl/Cmd combinations are the browser's own.
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const row = rows[rowIndex].tiles;
    let target: MapTile | undefined;
    switch (event.key) {
      case "ArrowRight":
        target = row[Math.min(col + 1, row.length - 1)];
        break;
      case "ArrowLeft":
        target = row[Math.max(col - 1, 0)];
        break;
      case "ArrowDown":
      case "ArrowUp": {
        const next = rows[rowIndex + (event.key === "ArrowDown" ? 1 : -1)];
        // The nearest tile: the same position in the neighbouring chapter, or its last one.
        target = next ? next.tiles[Math.min(col, next.tiles.length - 1)] : row[col];
        break;
      }
      case "Home":
        target = row[0];
        break;
      case "End":
        target = row[row.length - 1];
        break;
      case "Escape":
        if (selectedId) {
          event.preventDefault();
          setSelectedId(null);
        }
        return;
      default:
        return;
    }
    event.preventDefault();
    if (target) focusTile(target.topic.topicId);
  }

  function onPanelKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== "Escape") return;
    event.preventDefault();
    closePanel();
  }

  if (rows.length === 0) {
    return (
      <Card title={t("progress.map.title")} compact>
        <p className="text-sm text-ink-600 dark:text-ink-400">{t("common.nothingHere")}</p>
      </Card>
    );
  }

  return (
    <Card title={t("progress.map.title")} description={t("progress.map.description")} compact>
      <ul
        aria-label={t("progress.map.legend")}
        className="mb-2 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-ink-700 dark:text-ink-200"
      >
        {TOPIC_STATES.map((state) => (
          <li key={state} className="flex items-center gap-1.5">
            <StateSwatch state={state} />
            {t(`progress.map.state.${state}`)}
          </li>
        ))}
      </ul>
      <p className="mb-1 text-xs text-ink-600 dark:text-ink-400">
        {t("progress.map.secureDefinition")}
      </p>
      <p id={instructionsId} className="mb-3 text-xs text-ink-600 dark:text-ink-400">
        {t("progress.map.instructions")}
      </p>

      <div
        role="grid"
        aria-label={t("progress.map.title")}
        aria-describedby={instructionsId}
        className="border-t border-ink-200 dark:border-ink-800"
      >
        {rows.map((row, rowIndex) => (
          <div
            key={row.chapter.chapter}
            role="row"
            className="flex flex-wrap items-center gap-1 border-b border-ink-100 py-3 last:border-0 dark:border-ink-900"
          >
            <div role="rowheader" className="mb-2 w-full sm:mb-0 sm:w-64 sm:shrink-0 sm:pr-4">
              <p className="mb-1 text-sm font-medium text-ink-800 dark:text-ink-100">
                {chapterTitle(row.chapter.chapter, infoByChapter.get(row.chapter.chapter))}
              </p>
              <ChapterSummary chapter={row.chapter} />
            </div>
            {row.tiles.map((tile, col) => {
              const id = tile.topic.topicId;
              const isSelected = id === selectedId;
              return (
                <div key={id} role="gridcell" className="flex h-8 w-8 items-center justify-center">
                  <button
                    type="button"
                    ref={(element) => {
                      if (element) tileRefs.current.set(id, element);
                      else tileRefs.current.delete(id);
                    }}
                    tabIndex={id === tabStopId ? 0 : -1}
                    aria-label={labelOf(tile)}
                    title={labelOf(tile)}
                    aria-expanded={isSelected}
                    aria-controls={isSelected ? panelId : undefined}
                    onClick={() => setSelectedId(isSelected ? null : id)}
                    onFocus={() => setFocusId(id)}
                    onKeyDown={(event) => onTileKeyDown(event, rowIndex, col)}
                    className={clsx(
                      stateTileClass(tile.state),
                      isSelected &&
                        "ring-2 ring-ink-900 ring-offset-2 ring-offset-white dark:ring-ink-50 dark:ring-offset-ink-900",
                    )}
                  >
                    <StateGlyph state={tile.state} />
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div aria-live="polite">
        {selected && (
          <div
            id={panelId}
            role="group"
            aria-labelledby={`${panelId}-name`}
            onKeyDown={onPanelKeyDown}
            className="mt-3 rounded-lg border border-ink-200 bg-ink-50 p-4 dark:border-ink-700 dark:bg-ink-900"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3
                  id={`${panelId}-name`}
                  className="font-display text-base text-ink-900 dark:text-ink-50"
                >
                  {nameOf(selected.topic.topicId)}
                </h3>
                <p className="mt-1 flex items-center gap-2 text-sm font-medium text-ink-800 dark:text-ink-100">
                  <StateSwatch state={selected.state} />
                  {t(`progress.map.state.${selected.state}`)}
                </p>
              </div>
              <button
                type="button"
                onClick={closePanel}
                className="rounded px-2 py-1 text-sm text-ink-700 underline hover:text-ink-900 max-sm:min-h-11 dark:text-ink-200 dark:hover:text-ink-50"
              >
                {t("progress.map.detail.close")}
              </button>
            </div>
            <p className="mt-2 text-sm text-ink-700 dark:text-ink-200">
              {t("progress.map.detail.counts", {
                items: selected.topic.items,
                unaided: selected.topic.unaidedItems,
                assisted: selected.topic.assistedOnlyItems,
              })}
            </p>
            {selected.topic.retestDue && (
              <p className="text-sm text-ink-700 dark:text-ink-200">
                {t("progress.map.detail.retestDue", { date: dayLabel(selected.topic.retestDue) })}
              </p>
            )}
            <Link
              to={practiceDeepLink({ topics: [selected.topic.topicId], primary: true })}
              className="mt-3 inline-flex h-9 items-center justify-center rounded-lg bg-accent-700 px-4 text-sm font-medium text-white transition-colors hover:bg-accent-800 max-sm:min-h-11 dark:bg-accent-600 dark:hover:bg-accent-500"
            >
              {t("progress.map.detail.practise")}
            </Link>
          </div>
        )}
      </div>

      <ChaptersSection
        variant="tables"
        chapters={rows.map((row) => row.chapter)}
        chapterInfos={chapterInfos}
        topics={topics}
      />
    </Card>
  );
}

export default CourseMap;
